// Components==============
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// @ts-ignore
import Plyr from 'faqta-react-plyr';
import { useMediaQ } from 'hooks/useMediaQ';
import { usePermission } from 'hooks/usePermission';
import { useTranslate } from 'hooks/useTranslate';
import React, { useEffect, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { getSmartImage } from 'utilities/getSmartImage';
import { getVideoData } from 'utilities/getVideoData';
import Button from '../Button/Button';
import CircleButton from '../CircleButton/CircleButton';
import ImageSelector from '../ImageSelector/ImageSelector';
import './Summary.module.scss';
// =========================

export default function Summary({
  type,
  isOpen,
  setIsOpen,
  images,
  video,
  topLeftContent,
  topRightContent,
  bodyContent,
  isPeerReflection,
}: {
  type: 'project' | 'training' | 'reflection' | 'assessment';
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  images?: string[];
  video?: string;
  topLeftContent: React.ReactNode;
  topRightContent: React.ReactNode;
  bodyContent: React.ReactNode;
  isPeerReflection?: boolean;
}) {
  const [currentImage, setCurrentImage] = useState(images?.[0] || video || '');

  useEffect(() => {
    if ((!currentImage || currentImage === video) && images?.length)
      setCurrentImage(images?.[0]);
  }, [images?.length]);

  const translate = useTranslate();

  const query = useMediaQ('min', 1024);

  const { videoType, videoId, videoControls } = getVideoData(video);

  const isTeacher = usePermission(['Docent', 'super-admin']);

  const { projectId, checkpointId } = useParams() as {
    projectId?: string;
    checkpointId?: string;
  };

  const backLink =
    type === 'project'
      ? '/werkplaats/mijn-projecten'
      : type === 'training'
      ? '/werkplaats/mijn-trainingen'
      : type === 'assessment' && isTeacher
      ? '/groei/beoordelen'
      : type === 'assessment' && !isTeacher
      ? '/groei/mijn-beoordelingen'
      : `/groei/${
          checkpointId && !isPeerReflection
            ? `reflecteren/${projectId}`
            : 'mijn-reflecties'
        }`;

  return (
    <div styleName={`wrapper ${isOpen ? 'open' : 'not-open'}`}>
      {isOpen && (
        <>
          {(images || video) && (
            <div styleName="image-wrapper">
              <div styleName="image" />
              {currentImage.includes('https') ? (
                <div styleName="video">
                  <Plyr
                    type={videoType}
                    controls={videoControls}
                    videoId={videoId}
                    fullscreen={{
                      enabled: true,
                      fallback: true,
                      iosNative: true,
                    }}
                  />
                </div>
              ) : (
                <img
                  src={getSmartImage(currentImage)}
                  alt="background"
                  styleName="image"
                />
              )}
              {images && (
                <ImageSelector
                  images={images.map((i) => ({ path: i }))}
                  video={video}
                  currentImage={currentImage}
                  setCurrentImage={setCurrentImage}
                  shouldGoDown={currentImage === video}
                />
              )}
            </div>
          )}
        </>
      )}
      <div styleName="container">
        <div styleName="top-content">
          <div>
            <div styleName="back">
              <NavLink to={backLink}>
                {'< '}
                {type === 'training'
                  ? translate('general.terms.my-courses')
                  : type === 'project'
                  ? translate('general.terms.my-projects')
                  : type === 'assessment'
                  ? 'Beoordelen'
                  : translate('reflection.terms.my-reflections')}
              </NavLink>
            </div>
            {topLeftContent}
          </div>
          {!isOpen && (
            <div styleName="show-hide-button">
              {query ? (
                <Button color="grey" onClick={() => setIsOpen(true)}>
                  {type === 'training'
                    ? translate('course.terms.show')
                    : translate('general.terms.show-theme')}
                  <FontAwesomeIcon icon={faChevronDown} />
                </Button>
              ) : (
                <CircleButton
                  icon={faChevronDown}
                  color="grey-blue"
                  onClick={() => setIsOpen(true)}
                />
              )}
            </div>
          )}
          <div styleName="top-right-content">{topRightContent}</div>
        </div>
        {isOpen && <div styleName="body-content">{bodyContent}</div>}
        {isOpen && (
          <div styleName="show-hide-button">
            {query ? (
              <Button color="grey" onClick={() => setIsOpen(false)}>
                {type === 'training'
                  ? translate('course.terms.hide')
                  : translate('general.terms.hide-theme')}
                <FontAwesomeIcon icon={faChevronUp} />
              </Button>
            ) : (
              <CircleButton
                icon={faChevronUp}
                color="grey-blue"
                onClick={() => setIsOpen(false)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
